import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Tooltip, Popup, useMap, LayersControl, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import AccuracyLegend from './AccuracyLegend';
import { fetchCrossingVideo, updateGateCoordinates } from '../services/api';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, Save } from './Icons';
import './MapViewer.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const { BaseLayer } = LayersControl;

const getAccuracyColor = (accuracy, minAcc, maxAcc) => {
  if (!accuracy && accuracy !== 0) return '#999999';
  const range = maxAcc - minAcc;
  if (range === 0) return '#70C314';
  const normalized = (accuracy - minAcc) / range;
  if (normalized < 0.25) return '#70C314';
  if (normalized < 0.5) return '#FAEB36';
  if (normalized < 0.75) return '#FFA500';
  if (normalized < 0.9) return '#E81416';
  return '#111111';
};

const MapBoundsUpdater = ({ activities }) => {
  const map = useMap();
  useEffect(() => {
    if (activities.length > 0) {
      const bounds = L.latLngBounds(activities.map(a => [a.latitude, a.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [activities, map]);
  return null;
};

const KMZ_STYLE_DEFAULTS = {
  line: { color: '#8B5CF6', weight: 3, opacity: 0.7 },
  polygon: { color: '#8B5CF6', weight: 2, opacity: 0.6, fillColor: '#8B5CF6', fillOpacity: 0.15 },
  point: { radius: 5, color: '#8B5CF6', fillColor: '#8B5CF6', fillOpacity: 0.8, weight: 2 },
};

// ~7.5 feet per nudge
const NUDGE_FEET = 7.5;
const FEET_TO_METERS = 0.3048;
const METERS_PER_DEGREE_LAT = 111320;

function parseRawCoords(gate) {
  let coords = gate.coordinates;
  if (typeof coords === 'string') coords = JSON.parse(coords);
  return coords;
}

const MapViewer = ({
  gates,
  crossings,
  activities,
  selectedActivityIds,
  activityColors,
  visibleActivities,
  onToggleActivity,
  timeWindow,
  showPointMarkers,
  accuracyBounds,
  onAccuracyBoundsChange,
  kmzOverlays,
  onGatesUpdated,
  devices,
  gateAssignments,
  onDeviceSelect,
}) => {
  // ── Gate edit state ──
  const [editingGateId, setEditingGateId] = useState(null);
  const [editedCoords, setEditedCoords] = useState(null);
  // Snapshot of the gate's coords when editing began, drawn as a "ghost" so we
  // can see if the line has been translated/rotated. Cleared on save/cancel.
  const [originalCoords, setOriginalCoords] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editMessage, setEditMessage] = useState(null);

  // Helper to get devices assigned to a gate
  const getDevicesForGate = useCallback((gateId) => {
    if (!gateAssignments || !devices) return [];
    const deviceIds = gateAssignments[gateId] || [];
    return devices.filter(d => deviceIds.includes(d.deviceId));
  }, [gateAssignments, devices]);

  // Process gate lines, using edited coords when applicable
  const gateLines = useMemo(() => {
    return gates.map(gate => {
      try {
        let coords = parseRawCoords(gate);
        if (!Array.isArray(coords) || coords.length !== 2) return null;

        const isEditing = editingGateId === gate.gateId && editedCoords;
        const finalCoords = isEditing ? editedCoords : coords;

        return {
          id: gate.gateId,
          coords: [
            [finalCoords[0].latitude, finalCoords[0].longitude],
            [finalCoords[1].latitude, finalCoords[1].longitude]
          ]
        };
      } catch (err) {
        console.error('Error parsing gate coordinates:', err);
        return null;
      }
    }).filter(Boolean);
  }, [gates, editingGateId, editedCoords]);

  // ── Nudge logic ──
  const nudgeEndpoint = useCallback((direction) => {
    if (selectedEndpoint === null || !editedCoords) return;
    const coord = editedCoords[selectedEndpoint];
    const nudgeMeters = NUDGE_FEET * FEET_TO_METERS;
    const deltaLat = nudgeMeters / METERS_PER_DEGREE_LAT;
    const deltaLon = nudgeMeters / (METERS_PER_DEGREE_LAT * Math.cos(coord.latitude * Math.PI / 180));
    const newCoords = editedCoords.map(c => ({ ...c }));
    switch (direction) {
      case 'north': newCoords[selectedEndpoint].latitude += deltaLat; break;
      case 'south': newCoords[selectedEndpoint].latitude -= deltaLat; break;
      case 'east': newCoords[selectedEndpoint].longitude += deltaLon; break;
      case 'west': newCoords[selectedEndpoint].longitude -= deltaLon; break;
      default: break;
    }
    setEditedCoords(newCoords);
  }, [selectedEndpoint, editedCoords]);

  // Keyboard arrows
  useEffect(() => {
    if (!editingGateId || selectedEndpoint === null) return;
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowUp': e.preventDefault(); nudgeEndpoint('north'); break;
        case 'ArrowDown': e.preventDefault(); nudgeEndpoint('south'); break;
        case 'ArrowRight': e.preventDefault(); nudgeEndpoint('east'); break;
        case 'ArrowLeft': e.preventDefault(); nudgeEndpoint('west'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingGateId, selectedEndpoint, nudgeEndpoint]);

  const handleStartEditing = useCallback((gateId) => {
    const gate = gates.find(g => g.gateId === gateId);
    if (!gate) return;
    try {
      const coords = parseRawCoords(gate);
      setEditingGateId(gateId);
      setEditedCoords(coords.map(c => ({ latitude: c.latitude, longitude: c.longitude })));
      setOriginalCoords(coords.map(c => ({ latitude: c.latitude, longitude: c.longitude })));
      setSelectedEndpoint(null);
      setEditMessage(null);
    } catch (e) {
      console.error('Error starting gate edit:', e);
    }
  }, [gates]);

  const handleCancelEdit = useCallback(() => {
    setEditingGateId(null);
    setEditedCoords(null);
    setOriginalCoords(null);
    setSelectedEndpoint(null);
    setEditMessage(null);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingGateId || !editedCoords) return;
    setSavingEdit(true);
    setEditMessage(null);
    try {
      await updateGateCoordinates(editingGateId, editedCoords);
      setEditMessage({ type: 'success', text: 'Gate updated' });
      if (onGatesUpdated) onGatesUpdated();
      setTimeout(() => {
        setEditingGateId(null);
        setEditedCoords(null);
        setOriginalCoords(null);
        setSelectedEndpoint(null);
        setEditMessage(null);
      }, 1200);
    } catch (err) {
      console.error('Error saving gate:', err);
      setEditMessage({ type: 'error', text: 'Save failed' });
    } finally {
      setSavingEdit(false);
    }
  }, [editingGateId, editedCoords, onGatesUpdated]);

  const handleGateLineClick = useCallback((gateId) => {
    if (editingGateId === gateId) return;
    handleStartEditing(gateId);
  }, [editingGateId, handleStartEditing]);

  const handleEndpointClick = useCallback((gateId, idx) => {
    if (editingGateId === gateId) {
      setSelectedEndpoint(idx);
    } else {
      handleStartEditing(gateId);
    }
  }, [editingGateId, handleStartEditing]);

  // ── Existing state ──
  const activitiesById = useMemo(() => {
    const grouped = {};
    activities.forEach(point => {
      if (!grouped[point.activityId]) grouped[point.activityId] = [];
      grouped[point.activityId].push(point);
    });
    Object.keys(grouped).forEach(id => {
      grouped[id].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
    return grouped;
  }, [activities]);

  const { minAccuracy, maxAccuracy } = useMemo(() => {
    if (accuracyBounds.min !== null && accuracyBounds.max !== null) {
      return { minAccuracy: accuracyBounds.min, maxAccuracy: accuracyBounds.max };
    }
    const accuracies = activities.map(a => a.accuracy).filter(a => a !== null && a !== undefined && !isNaN(a));
    if (accuracies.length === 0) return { minAccuracy: 0, maxAccuracy: 1 };
    return { minAccuracy: Math.min(...accuracies), maxAccuracy: Math.max(...accuracies) };
  }, [activities, accuracyBounds]);
  
  const center = useMemo(() => {
    if (activities.length > 0) return [activities[0].latitude, activities[0].longitude];
    return [37.7749, -122.4194];
  }, [activities]);

  const [crossingVideos, setCrossingVideos] = useState({});

  const crossingKey = useCallback((crossing) =>
    `${crossing.gateId || crossing.gate_id}::${crossing.activityId || crossing.activity_id}`, []);

  const handleCrossingClick = useCallback(async (crossing) => {
    const key = crossingKey(crossing);
    if (crossingVideos[key]) return;
    const gateId = crossing.gateId || crossing.gate_id;
    const activityId = crossing.activityId || crossing.activity_id;
    if (!gateId || !activityId) return;
    setCrossingVideos(prev => ({ ...prev, [key]: { loading: true } }));
    try {
      const data = await fetchCrossingVideo(gateId, activityId);
      setCrossingVideos(prev => ({ ...prev, [key]: { loading: false, data } }));
    } catch (err) {
      setCrossingVideos(prev => ({ ...prev, [key]: { loading: false, error: err.message } }));
    }
  }, [crossingVideos, crossingKey]);

  const starIcon = (color) => L.divIcon({
    html: `<div style="font-size:20px;color:${color};cursor:pointer;" title="Click to check video">★</div>`,
    className: 'star-marker',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });

  const displayActivities = useMemo(() => {
    const { head: headCutoff, tail: tailCutoff } = timeWindow;
    const visible = {};
    Object.entries(activitiesById).forEach(([id, points]) => {
      if (!visibleActivities.has(id)) return;
      if (headCutoff === null && tailCutoff === null) {
        visible[id] = { points, isTimeTruncated: false };
        return;
      }
      let startIdx = 0;
      let endIdx = points.length;
      if (tailCutoff !== null) {
        startIdx = points.findIndex(p => new Date(p.timestamp).getTime() >= tailCutoff);
        if (startIdx === -1) startIdx = points.length;
      }
      if (headCutoff !== null) {
        const headIdx = points.findIndex(p => new Date(p.timestamp).getTime() > headCutoff);
        if (headIdx !== -1) endIdx = headIdx;
      }
      const filtered = startIdx < endIdx ? points.slice(startIdx, endIdx) : [];
      visible[id] = { points: filtered, isTimeTruncated: filtered.length < points.length };
    });
    return visible;
  }, [activitiesById, timeWindow, visibleActivities]);

  return (
    <div className="map-viewer">
      <MapContainer 
        center={center} 
        zoom={15} 
        style={{ height: '100%', width: '100%' }}
        maxZoom={30}
        zoomDelta={0.2}
        zoomSnap={0.2}
      >
        <LayersControl position="topright">
          <BaseLayer checked name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={30}
            />
          </BaseLayer>
          <BaseLayer name="Satellite">
            <TileLayer
              attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={30}
            />
          </BaseLayer>
          <BaseLayer name="Topo Map">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              maxZoom={30}
            />
          </BaseLayer>
        </LayersControl>
        
        <MapBoundsUpdater activities={activities} />

        {/* KMZ / KML overlay layers */}
        {kmzOverlays && kmzOverlays
          .filter(overlay => overlay.visible)
          .flatMap(overlay => overlay.groups
            .filter(group => group.visible)
            .map(group => (
              <GeoJSON
                key={`kmz-${group.id}`}
                data={{ type: 'FeatureCollection', features: group.features }}
                style={(feature) => {
                  const props = feature.properties || {};
                  const geomType = feature.geometry?.type || '';
                  const opacity = overlay.opacity;
                  if (geomType.includes('Polygon')) {
                    return {
                      color: props['stroke'] || KMZ_STYLE_DEFAULTS.polygon.color,
                      weight: props['stroke-width'] || KMZ_STYLE_DEFAULTS.polygon.weight,
                      opacity: (props['stroke-opacity'] ?? KMZ_STYLE_DEFAULTS.polygon.opacity) * opacity,
                      fillColor: props['fill'] || KMZ_STYLE_DEFAULTS.polygon.fillColor,
                      fillOpacity: (props['fill-opacity'] ?? KMZ_STYLE_DEFAULTS.polygon.fillOpacity) * opacity,
                    };
                  }
                  return {
                    color: props['stroke'] || KMZ_STYLE_DEFAULTS.line.color,
                    weight: props['stroke-width'] || KMZ_STYLE_DEFAULTS.line.weight,
                    opacity: (props['stroke-opacity'] ?? KMZ_STYLE_DEFAULTS.line.opacity) * opacity,
                  };
                }}
                pointToLayer={(feature, latlng) => {
                  const props = feature.properties || {};
                  const opacity = overlay.opacity;
                  return L.circleMarker(latlng, {
                    ...KMZ_STYLE_DEFAULTS.point,
                    color: props['stroke'] || KMZ_STYLE_DEFAULTS.point.color,
                    fillColor: props['fill'] || KMZ_STYLE_DEFAULTS.point.fillColor,
                    fillOpacity: KMZ_STYLE_DEFAULTS.point.fillOpacity * opacity,
                    opacity: opacity,
                  });
                }}
                onEachFeature={(feature, layer) => {
                  const name = feature.properties?.name;
                  const desc = feature.properties?.description;
                  if (name || desc) {
                    layer.bindTooltip(name || '', { sticky: true });
                    if (desc) {
                      layer.bindPopup(`<div style="max-width:280px"><strong>${name || ''}</strong>${desc ? '<br/>' + desc : ''}</div>`);
                    }
                  }
                }}
              />
            ))
          )}

        {/* Draw activities */}
        {Object.entries(displayActivities).map(([activityId, data], index) => {
          const { points, isTimeTruncated } = data;
          const color = activityColors[activityId] || '#fc5f21';
          const isTimeFiltered = timeWindow.head !== null || timeWindow.tail !== null;
          const lineOpacity = isTimeFiltered ? 0.8 : 0.6;
          
          return (
            <React.Fragment key={`activity-${activityId}`}>
              <Polyline
                positions={points.map(p => [p.latitude, p.longitude])}
                color={color}
                weight={4}
                opacity={lineOpacity}
              />
              
              {showPointMarkers && points.map((point, idx) => {
                const accColor = getAccuracyColor(point.accuracy, minAccuracy, maxAccuracy);
                const isLeadingPoint = isTimeTruncated && idx === points.length - 1;
                
                return (
                  <CircleMarker
                    key={`point-${activityId}-${point.timestamp}`}
                    center={[point.latitude, point.longitude]}
                    radius={isLeadingPoint ? 6 : 3}
                    color={isLeadingPoint ? color : accColor}
                    fillColor={isLeadingPoint ? color : accColor}
                    fillOpacity={isLeadingPoint ? 1 : 0.8}
                    weight={isLeadingPoint ? 3 : 2}
                    opacity={1}
                  >
                    <Tooltip>
                      Activity: {activityId}<br />
                      User: {point.userId || 'unknown'}<br />
                      Time: {new Date(point.timestamp).toLocaleTimeString()}<br />
                      Accuracy: {point.accuracy !== null && point.accuracy !== undefined ? 
                        point.accuracy.toFixed(2) : 'N/A'}
                    </Tooltip>
                  </CircleMarker>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Draw gate lines */}
        {gateLines.map((gate) => {
          const isEditing = editingGateId === gate.id;
          const gateColor = isEditing ? '#2196F3' : 'black';
          const gateWeight = isEditing ? 5 : 3;
          const ghostCoords = isEditing && originalCoords ? [
            [originalCoords[0].latitude, originalCoords[0].longitude],
            [originalCoords[1].latitude, originalCoords[1].longitude]
          ] : null;

          return (
            <React.Fragment key={`gate-${gate.id}-${isEditing}-${selectedEndpoint}`}>
              {/* Ghost of the gate's original position while editing, so we can
                  tell if the whole line has drifted (translated/rotated). */}
              {ghostCoords && (
                <>
                  <Polyline
                    positions={ghostCoords}
                    pathOptions={{ color: '#888', weight: 3, opacity: 0.55, dashArray: '6 6', interactive: false }}
                  />
                  {ghostCoords.map((coord, gIdx) => (
                    <CircleMarker
                      key={`gate-ghost-${gate.id}-${gIdx}`}
                      center={coord}
                      radius={5}
                      pathOptions={{ color: '#888', fillColor: '#888', fillOpacity: 0.35, opacity: 0.55, weight: 1, interactive: false }}
                    />
                  ))}
                </>
              )}
              <Polyline
                positions={gate.coords}
                pathOptions={{ color: gateColor, weight: gateWeight, opacity: 1 }}
                eventHandlers={{
                  click: () => handleGateLineClick(gate.id)
                }}
              >
                <Tooltip sticky>{gate.id}{isEditing ? ' (editing)' : ''}</Tooltip>
              </Polyline>
              
              {gate.coords.map((coord, cIdx) => {
                const isActiveEndpoint = isEditing && selectedEndpoint === cIdx;
                return (
                  <CircleMarker
                    key={`gate-point-${gate.id}-${cIdx}-${isEditing}-${isActiveEndpoint}`}
                    center={coord}
                    radius={isActiveEndpoint ? 9 : (isEditing ? 7 : 5)}
                    pathOptions={{
                      color: isActiveEndpoint ? '#fff' : (isEditing ? '#fff' : 'black'),
                      fillColor: isActiveEndpoint ? '#e91e63' : (isEditing ? '#2196F3' : 'black'),
                      fillOpacity: 1,
                      weight: isActiveEndpoint ? 3 : 2
                    }}
                    eventHandlers={{
                      click: () => handleEndpointClick(gate.id, cIdx)
                    }}
                  >
                    {isEditing && (
                      <Tooltip permanent={isActiveEndpoint} direction="top">
                        {cIdx === 0 ? 'A' : 'B'}{isActiveEndpoint ? ' (selected)' : ''}
                      </Tooltip>
                    )}
                  </CircleMarker>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Draw crossings */}
        {crossings.map((crossing, index) => {
          if (!visibleActivities.has(crossing.activityId)) return null;

          const color = activityColors[crossing.activityId] || '#fc5f21';
          const key = crossingKey(crossing);
          const videoState = crossingVideos[key];

          const renderVideoContent = () => {
            if (!videoState) return <span style={{color:'#aaa',fontSize:'11px'}}>Click to check video</span>;
            if (videoState.loading) return <span style={{color:'#aaa',fontSize:'11px'}}>Loading...</span>;
            if (videoState.error) return <span style={{color:'#f55',fontSize:'11px'}}>Error: {videoState.error}</span>;
            const d = videoState.data;
            if (!d) return null;
            if (d.status === 'completed' && d.url) {
              return (
                <a href={d.url} target="_blank" rel="noopener noreferrer"
                  style={{color:'#fc5f21',fontWeight:'bold',fontSize:'12px'}}>
                  ▶ Watch Video ({d.sliceDuration}s)
                </a>
              );
            }
            if (d.status === 'failed') return <span style={{color:'#f88',fontSize:'11px'}}>Failed: {d.reason}</span>;
            if (d.status === 'processing') return <span style={{color:'#FFC107',fontSize:'11px'}}>Processing...</span>;
            if (d.status === 'pending') return <span style={{color:'#aaa',fontSize:'11px'}}>Pending</span>;
            return <span style={{color:'#aaa',fontSize:'11px'}}>{d.message || 'No video found'}</span>;
          };

          return (
            <Marker
              key={`crossing-${crossing.gate_crossingId || index}`}
              position={[crossing.intersection_lat, crossing.intersection_lon]}
              icon={starIcon(color)}
              eventHandlers={{ click: () => handleCrossingClick(crossing) }}
            >
              <Popup>
                <div style={{fontFamily:'sans-serif',lineHeight:'1.6'}}>
                  <div style={{fontWeight:'bold',marginBottom:'4px'}}>
                    Gate: {crossing.gateId || crossing.gate_id || 'unknown'}
                  </div>
                  <div style={{fontSize:'11px',color:'#555',marginBottom:'4px'}}>
                    Activity: {crossing.activityId}<br />
                    User: {crossing.userId || crossing.user_id || 'unknown'}<br />
                    Time: {crossing.timestamp ? new Date(crossing.timestamp).toLocaleString() : 'N/A'}
                  </div>
                  {(() => {
                    const gateId = crossing.gateId || crossing.gate_id;
                    const triggeredDevices = getDevicesForGate(gateId);
                    if (triggeredDevices.length === 0) return null;
                    return (
                      <div style={{fontSize:'11px',marginTop:'8px',paddingTop:'8px',borderTop:'1px solid #ddd'}}>
                        <div style={{fontWeight:'bold',marginBottom:'4px'}}>Triggered Devices:</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                          {triggeredDevices.map(device => (
                            <button
                              key={device.deviceId}
                              onClick={() => onDeviceSelect?.(device.deviceId)}
                              style={{
                                fontSize:'10px',
                                padding:'3px 6px',
                                backgroundColor:'#fc5f21',
                                color:'white',
                                border:'none',
                                borderRadius:'3px',
                                cursor:'pointer'
                              }}
                              title={`Go to ${device.deviceId}`}
                            >
                              {device.deviceName || device.deviceId}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{marginTop:'8px'}}>{renderVideoContent()}</div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Gate edit panel (floating over the map) */}
      {editingGateId && (
        <div className="mv-gate-edit-panel">
          <div className="mv-edit-header">
            <strong>{editingGateId}</strong>
            <button className="mv-edit-close" onClick={handleCancelEdit}><X size={14} /></button>
          </div>

          {/* Gate metadata */}
          {(() => {
            const gate = gates.find(g => g.gateId === editingGateId);
            if (!gate) return null;
            return (
              <div className="mv-gate-metadata">
                {gate.trail_name && <div className="metadata-line"><strong>Trail:</strong> {gate.trail_name}</div>}
                {gate.placement && <div className="metadata-line"><strong>Placement:</strong> {gate.placement}</div>}
                {gate.location_name && <div className="metadata-line"><strong>Location:</strong> {gate.location_name}</div>}
                {(() => {
                  const triggeredDevices = getDevicesForGate(editingGateId);
                  if (triggeredDevices.length === 0) return null;
                  return (
                    <div className="metadata-section">
                      <div className="metadata-label">Triggered Devices ({triggeredDevices.length})</div>
                      <div className="device-list">
                        {triggeredDevices.map(device => (
                          <button
                            key={device.deviceId}
                            className="device-link"
                            onClick={() => onDeviceSelect?.(device.deviceId)}
                            title={`Go to ${device.deviceId}`}
                          >
                            {device.deviceName || device.deviceId}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          <div className="mv-endpoint-btns">
            <button
              className={`mv-ep-btn ${selectedEndpoint === 0 ? 'active' : ''}`}
              onClick={() => setSelectedEndpoint(0)}
            >A</button>
            <button
              className={`mv-ep-btn ${selectedEndpoint === 1 ? 'active' : ''}`}
              onClick={() => setSelectedEndpoint(1)}
            >B</button>
          </div>

          {selectedEndpoint !== null && editedCoords && (
            <div className="mv-coord-display">
              {editedCoords[selectedEndpoint].latitude.toFixed(7)}, {editedCoords[selectedEndpoint].longitude.toFixed(7)}
            </div>
          )}

          <div className="mv-dpad">
            <div />
            <button className="mv-dpad-btn" onClick={() => nudgeEndpoint('north')} disabled={selectedEndpoint === null} title="North (~7.5 ft)">
              <ChevronUp size={16} />
            </button>
            <div />
            <button className="mv-dpad-btn" onClick={() => nudgeEndpoint('west')} disabled={selectedEndpoint === null} title="West (~7.5 ft)">
              <ChevronLeft size={16} />
            </button>
            <div className="mv-dpad-label">~7.5 ft</div>
            <button className="mv-dpad-btn" onClick={() => nudgeEndpoint('east')} disabled={selectedEndpoint === null} title="East (~7.5 ft)">
              <ChevronRight size={16} />
            </button>
            <div />
            <button className="mv-dpad-btn" onClick={() => nudgeEndpoint('south')} disabled={selectedEndpoint === null} title="South (~7.5 ft)">
              <ChevronDown size={16} />
            </button>
            <div />
          </div>

          {selectedEndpoint === null && (
            <div className="mv-edit-hint">Click an endpoint to select it</div>
          )}
          {selectedEndpoint !== null && (
            <div className="mv-edit-hint">Use arrows or keyboard to nudge</div>
          )}

          {editMessage && (
            <div className={`mv-edit-msg ${editMessage.type}`}>{editMessage.text}</div>
          )}

          <div className="mv-edit-actions">
            <button className="mv-act-cancel" onClick={handleCancelEdit}>Cancel</button>
            <button className="mv-act-save" onClick={handleSaveEdit} disabled={savingEdit}>
              <Save size={14} />
              {savingEdit ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Accuracy Legend */}
      {activities.length > 0 && (
        <AccuracyLegend
          minAccuracy={minAccuracy}
          maxAccuracy={maxAccuracy}
          onBoundsChange={onAccuracyBoundsChange}
        />
      )}
    </div>
  );
};

export default MapViewer;
